// Tableau to Amazon Quick page content. Same shape as domo.js; rendered by
// PlatformMigrationContent (components/BiPlatformPage).
export const tableauPage = {
  whyMove: {
    eyebrow: 'Why move',
    title: 'Why teams move from Tableau to Amazon Quick',
    intro: 'The reasons we hear most from Tableau customers running on AWS.',
    cards: [
      { title: 'Your data is already on AWS', description: 'Stop copying Redshift, S3, and Athena data into Hyper extracts on a refresh schedule. Quick reads it where it lives.' },
      { title: 'AI agents on the same data', description: 'Once workbooks are on Quick, Chat Agents, Flows, and Research work on the same governed datasets.' },
      { title: 'Spend that counts on AWS', description: 'Quick and the AWS services under it can draw down your existing AWS commitment instead of separate Creator, Explorer, and Viewer licenses.' },
      { title: 'Security in one place', description: 'Project permissions, user filters, and data policies move to IAM, SSO, and row-level security on AWS.' },
    ],
  },

  metadata: {
    id: 'metadata',
    eyebrow: 'Built on your Tableau metadata',
    title: 'We read your Tableau site through its APIs',
    intro: 'Instead of opening every workbook, we pull metadata straight from Tableau Server or Tableau Cloud. Discovery takes days, every decision traces back to real usage, and the same metadata drives conversion, testing, and training.',
    columns: ['What we pull', 'From', 'How we use it'],
    rows: [
      { what: 'Sites, projects, workbooks, and views', from: 'Tableau REST API', use: 'Build the full inventory and group content by project and business area' },
      { what: 'Published and embedded data sources', from: 'Tableau Metadata API (GraphQL)', use: 'Size the data landing in Amazon S3 or Redshift and rebuild datasets on Quick' },
      { what: 'Calculated fields, LOD expressions, and table calcs', from: 'Metadata API and workbook XML (.twb)', use: 'Convert calculations to Quick calculated fields and spot duplicate logic across workbooks' },
      { what: 'Custom SQL, joins, and Prep flows', from: 'Metadata API and Tableau Prep flow files', use: 'Map lineage end to end and rebuild as AWS Glue or Redshift SQL' },
      { what: 'Permissions, user filters, and data policies', from: 'REST API and data source definitions', use: 'Recreate the same row-level security and permissions on Amazon Quick' },
      { what: 'View usage and extract refreshes', from: 'Repository or Admin Insights and Activity Log', use: 'Find the most-used and unused workbooks to rank waves and plan refresh schedules' },
      { what: 'Connections and database servers', from: 'Metadata API and REST API', use: 'Plan connectivity for every source system' },
    ],
    benefits: {
      eyebrow: 'What you get from it',
      items: [
        { title: 'Full inventory and lineage', description: 'Every view traced back through its data source, custom SQL, and Prep flows to the source system.' },
        { title: 'Complexity score per workbook', description: 'LOD expressions, table calcs, parameters, and user filters scored, so each wave is sized realistically.' },
        { title: 'Wave plan from real usage', description: 'Waves ranked by view counts and active users, not by who asks loudest.' },
        { title: 'UAT test cases, generated', description: 'Expected values pulled from Tableau through the API and compared with Quick automatically.' },
      ],
      note: {
        lead: 'Read-only access.',
        text: 'Your Tableau admin issues a personal access token for a service account and enables the Metadata API. We only read; nothing in Tableau changes until you retire it.',
      },
    },
  },

  process: {
    eyebrow: 'How a Tableau migration runs',
    title: 'Automated and AI-led, wave by wave',
    intro: 'Steps 3 to 5 repeat for each wave, so your team reviews exceptions instead of testing every workbook.',
    steps: [
      { tag: 'Automated', title: 'Automated assessment', description: 'Metadata pulled through the Tableau APIs: projects, workbooks, data sources, calculations, permissions, and usage.' },
      { tag: 'AI-led', title: 'Wave planning', description: 'Workbooks grouped into waves by usage, shared data sources, and project. Unused workbooks are flagged to retire.' },
      { tag: 'Automated', title: 'Automated conversion', description: 'Extracts and Prep flows rebuilt as AWS pipelines, calculations converted to calculated fields, and dashboards rebuilt as Quick Sight sheets.' },
      { tag: 'AI-led', title: 'AI-led UAT and QA', description: 'Every migrated dashboard compared with its Tableau original. Your team signs off on exceptions only.' },
      { tag: 'Automated', title: 'Enablement and training', description: 'Training per wave, built from who uses which Tableau workbooks today.' },
    ],
  },

  afterMigration: {
    eyebrow: 'After the migration',
    title: 'More than dashboards: AI on the same data',
    items: [
      { title: 'Ask the data', description: 'A Chat Agent answers questions on the same datasets your old Tableau workbooks used, with sources.' },
      { title: 'Automate the follow-up', description: 'Quick Flows turn a threshold alert into a task, an email, or a ticket.' },
      { title: 'Research with context', description: 'Quick Research blends your internal numbers with outside sources in one report.' },
    ],
  },

  showFunding: true,
};
